import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field, FormSpy } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { useTranslation } from 'react-i18next';

import WrappedFormSelect from './WrappedFormSelect';
import FormInput from './FormInput';
import FormInputHolder from './FormInputHolder';
import FormLabel from './FormLabel';
import { ArrowDown } from '../icons';
import { Error } from './BaseForm';
import { required } from '../../helpers/formValidators';

import './FormSelect.scss';

const FormSelect = ({
  options: initialOptions = [],
  name,
  id,
  otherName,
  otherLabel,
  includeOther = false,
  disabled = false,
  validate,
  onChange,
  value,
  initialValue,
  placeholder
}) => {
  const { t } = useTranslation();

  const options = useMemo(() => {
    const baseOptions = [...initialOptions];
    if (includeOther) {
      baseOptions.push({
        label: t('profile.profileOtherFillInBelow', 'Other (fill in below)'),
        value: 'other'
      });
    }
    return baseOptions;
  }, [initialOptions, includeOther, t]);

  return (
    <>
      <div className="form-select">
        <Field
          id={id}
          name={name}
          component={WrappedFormSelect}
          options={options}
          disabled={disabled}
          tabIndex={disabled ? '-1' : undefined}
          validate={validate}
          onChange={onChange}
          value={value}
          initialValue={initialValue}
          placeholder={placeholder}
          arrowRenderer={() => (
            <ArrowDown
              className="form-select__expand"
              width="2.4rem"
              height="2.4rem"
            />
          )}
        />

        {includeOther && otherName && (
          <Field name={otherName} subscription={{}}>
            {({ input }) => (
              <OnChange name={name}>
                {(val) => {
                  if (val !== 'other' && input.value) {
                    input.onChange(null);
                  }
                }}
              </OnChange>
            )}
          </Field>
        )}

        <FormSpy subscription={{ values: true }}>
          {({ values }) =>
            includeOther && values?.[name] === 'other' ? (
              <FormInputHolder>
                <FormInput
                  id={`${name}.other`}
                  name={otherName}
                  type="text"
                  validate={required}
                  maxLength={100}
                  labelRenderer={() => (
                    <FormLabel>
                      {otherLabel || t('global.other', 'Other')}
                    </FormLabel>
                  )}
                />
              </FormInputHolder>
            ) : null
          }
        </FormSpy>
      </div>

      <Error name={name} />
    </>
  );
};

FormSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string
    })
  ).isRequired,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  otherName: PropTypes.string,
  otherLabel: PropTypes.string,
  includeOther: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  validate: PropTypes.func,
  onChange: PropTypes.func,
  initialValue: PropTypes.any,
  value: PropTypes.any
};

export default FormSelect;
